/**
 * Agent Ticker hook.
 *
 * Polls subagent sessions across all connected gateways and
 * provides a unified list for the Agent Ticker UI component.
 *
 * Subagent sessions are identified by their session key structure
 * (contains "subagent"). Each ticker item tracks:
 * - Session metadata (key, label, status)
 * - Gateway origin (for multi-gateway support)
 *
 * Items remain visible until explicitly dismissed by the user.
 * Dismissed items reappear if they become active again (new activity).
 *
 * On initial load, only sessions from the last 24 hours are shown
 * to prevent ancient sessions from cluttering the ticker.
 *
 * Also listens for incoming chat events to trigger immediate refreshes
 * when a subagent session produces output.
 *
 * The peek overlay supports **live streaming**: when open, it subscribes
 * to WSClient events filtered by sessionKey to stream deltas in real-time
 * and finalize messages as they complete.
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type { WSClient } from '../ws/client';
import type { GatewayConfig, GatewayStatus } from '../types/gateway';
import type { SessionRow } from '../types/sessions';
import type {
  EventFrame,
  HistoryMessage,
  ChatEventPayload,
  ChatEventDelta,
  ChatEventFinal,
} from '../types/protocol';
import { listSessions } from '../chat/sessions';
import { fetchChatHistory } from '../chat/history';
import { normalizeContent } from '../utils/content';

// ── Types ──

export type TickerStatus = 'running' | 'completed' | 'error';

export interface TickerItem {
  /** Unique identifier: `${gatewayId}::${sessionKey}` */
  id: string;
  /** Session key */
  sessionKey: string;
  /** Gateway ID this session belongs to */
  gatewayId: string;
  /** Gateway display name */
  gatewayName: string;
  /** Display label (agent label or extracted name, truncated) */
  label: string;
  /** Current status */
  status: TickerStatus;
  /** When this item was first seen (for ordering) */
  firstSeenAt: number;
  /** When this item completed (null if still running) */
  completedAt: number | null;
  /** Last message preview (if available) */
  lastMessagePreview?: string;
  /** Last activity timestamp from the session */
  updatedAt: number | null;
  /** Whether the session has been cleaned up by the gateway (no longer in poll results) */
  sessionGone: boolean;
  /** Timestamp when the session was first detected as gone (null otherwise) */
  goneAt: number | null;
  /** Heat level 0-4 based on recency of activity. 4=hot (just active), 0=cold (idle/gone) */
  heatLevel: number;
  /** Nearest visible ancestor session key reconstructed from session history (when available) */
  parentSessionKey?: string | null;
}

export interface PeekData {
  /** Ticker item being peeked */
  item: TickerItem;
  /** Recent messages for this session (history + finalized streaming) */
  messages: HistoryMessage[];
  /** Whether messages are loading */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Currently streaming assistant text (null when not streaming) */
  streamingContent: string | null;
}

export interface UseAgentTickerReturn {
  /** Sorted list of ticker items (running first, then by recency) */
  items: TickerItem[];
  /** Whether any items are currently running */
  hasRunning: boolean;
  /** Whether any completed items exist in the current list */
  hasCompleted: boolean;
  /** Currently peeked item (null if none) */
  peek: PeekData | null;
  /** Open peek for a ticker item */
  openPeek: (item: TickerItem) => void;
  /** Close peek overlay */
  closePeek: () => void;
  /** Dismiss a completed ticker item manually */
  dismiss: (id: string) => void;
  /** Dismiss all completed ticker items at once */
  dismissAllCompleted: () => void;
  /** Force refresh all ticker data */
  refresh: () => void;
  /** Set of subscribed session keys (shown in main chat) */
  subscribedSessions: Set<string>;
  /** Subscribe a session to show in main chat */
  subscribe: (sessionKey: string) => void;
  /** Unsubscribe a session from main chat */
  unsubscribe: (sessionKey: string) => void;
  /** Check if a session is subscribed */
  isSubscribed: (sessionKey: string) => boolean;
}

// ── Constants ──

/** How often to poll for subagent sessions (ms) */
const POLL_INTERVAL_MS = 12_000;

/** Maximum age of sessions to show on initial load (24 hours) */
const INITIAL_LOAD_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Number of recent messages to fetch for peek */
const PEEK_MESSAGE_COUNT = 5;

/** Session storage key for dismissed items */
const DISMISSED_STORAGE_KEY = 'agent-ticker-dismissed';

/** Session storage key for subscribed sessions (show in main chat) */
const SUBSCRIBED_STORAGE_KEY = 'agent-ticker-subscribed';

/** Delay before auto-dismissing a gone session (ms) */
const AUTO_DISMISS_DELAY_MS = 30_000;

/** Max messages to keep in peek (prevent unbounded growth) */
const PEEK_MAX_MESSAGES = 30;

/** Page size when scanning session history for hierarchy reconstruction */
const RELATION_HISTORY_PAGE_SIZE = 100;

/** Minimum time between retry scans for the same session after a failure */
const RELATION_SCAN_INTERVAL_MS = 30_000;

// ── Helpers ──

/**
 * Check if a session key represents a subagent session.
 * Subagent keys contain "subagent" in their structure,
 * e.g., "agent:main:subagent:uuid-here"
 */
function isSubagentSession(key: string): boolean {
  return key.includes('subagent');
}

/**
 * Extract a human-readable label from a subagent session.
 * Tries: label → displayName → derivedTitle → last segment of key.
 */
function extractLabel(session: SessionRow): string {
  if (session.label) return session.label;
  if (session.displayName) return session.displayName;
  if (session.derivedTitle) return session.derivedTitle;

  // Fallback: parse from key — e.g. "agent:main:subagent:fix-tts" → "fix-tts"
  const parts = session.key.split(':');
  const subIdx = parts.indexOf('subagent');
  if (subIdx >= 0 && subIdx + 1 < parts.length) {
    const raw = parts.slice(subIdx + 1).join(':');
    // Truncate UUIDs — show first 8 chars
    if (raw.length > 20) return raw.slice(0, 8) + '…';
    return raw;
  }

  return parts[parts.length - 1] ?? 'agent';
}

/**
 * Determine ticker status from session data.
 * Uses updatedAt recency and sendPolicy as heuristics.
 */
function inferStatus(session: SessionRow): TickerStatus {
  // If sendPolicy is deny, the session is likely completed or locked
  if (session.sendPolicy === 'deny') return 'completed';

  // Check recency — sessions inactive for >2 minutes are likely completed
  if (session.updatedAt) {
    const ageMs = Date.now() - session.updatedAt;
    if (ageMs > 2 * 60 * 1000) return 'completed';
  }

  return 'running';
}

/**
 * Load dismissed item IDs from sessionStorage.
 * Returns a Map of id → dismissedAt timestamp.
 */
function loadDismissedFromStorage(): Map<string, number> {
  try {
    const raw = sessionStorage.getItem(DISMISSED_STORAGE_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw) as Record<string, number>;
    return new Map(Object.entries(parsed));
  } catch {
    return new Map();
  }
}

/**
 * Save dismissed item IDs to sessionStorage.
 */
function saveDismissedToStorage(dismissed: Map<string, number>): void {
  try {
    const obj = Object.fromEntries(dismissed);
    sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // sessionStorage unavailable — degrade silently
  }
}

/**
 * Load subscribed session keys from sessionStorage.
 */
function loadSubscribedFromStorage(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SUBSCRIBED_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as string[];
    return new Set(parsed);
  } catch {
    return new Set();
  }
}

/**
 * Save subscribed session keys to sessionStorage.
 */
function saveSubscribedToStorage(subscribed: Set<string>): void {
  try {
    sessionStorage.setItem(SUBSCRIBED_STORAGE_KEY, JSON.stringify([...subscribed]));
  } catch {
    // sessionStorage unavailable — degrade silently
  }
}

/**
 * Compute heat level (0-4) based on how recently the session was active.
 * 4 = active in last 10 seconds (hot red)
 * 3 = active in last 30 seconds (orange)
 * 2 = active in last 2 minutes (yellow/warm)
 * 1 = active in last 5 minutes (blue/cool)
 * 0 = older than 5 minutes or gone (grey/cold)
 */
function computeHeatLevel(updatedAt: number | null, sessionGone: boolean): number {
  if (sessionGone) return 0;
  if (!updatedAt) return 0;
  const ageMs = Date.now() - updatedAt;
  if (ageMs < 10_000) return 4;
  if (ageMs < 30_000) return 3;
  if (ageMs < 120_000) return 2;
  if (ageMs < 300_000) return 1;
  return 0;
}

interface HierarchyRelationScan {
  childSessionKeys: string[];
  parentSessionKey: string | null;
  newestCursor: string | null;
  fullyScanned: boolean;
}

interface RelationCacheEntry extends HierarchyRelationScan {
  lastScannedAt: number;
  lastScannedUpdatedAt: number | null;
  scanFailed: boolean;
}

function compositeSessionId(gatewayId: string, sessionKey: string): string {
  return `${gatewayId}::${sessionKey}`;
}

function historyCursor(message: HistoryMessage | undefined): string | null {
  return message?.ts ?? null;
}

function extractHierarchyRelations(messages: HistoryMessage[]): {
  childSessionKeys: string[];
  parentSessionKey: string | null;
} {
  const childKeys = new Set<string>();
  const childKeyPattern = /"childSessionKey"\s*:\s*"([^"]+)"/g;
  const requesterPatterns = [
    /(?:^|\n)\s*Requester session:\s*([A-Za-z0-9:_-]+)/i,
    /"requesterSessionKey"\s*:\s*"([^"]+)"/i,
  ];

  let parentSessionKey: string | null = null;

  for (const message of messages) {
    const content = normalizeContent(message.content);

    for (const match of content.matchAll(childKeyPattern)) {
      if (match[1]) childKeys.add(match[1]);
    }

    if (!parentSessionKey) {
      for (const pattern of requesterPatterns) {
        const match = content.match(pattern);
        if (match?.[1]) {
          parentSessionKey = match[1];
          break;
        }
      }
    }
  }

  return {
    childSessionKeys: [...childKeys],
    parentSessionKey,
  };
}

async function discoverHierarchyRelations(params: {
  client: WSClient;
  sessionKey: string;
  stopAtCursor?: string | null;
}): Promise<HierarchyRelationScan> {
  const { client, sessionKey, stopAtCursor = null } = params;
  const childSessionKeys = new Set<string>();
  let parentSessionKey: string | null = null;
  let before: string | undefined;
  let newestCursor: string | null = null;
  let fullyScanned = false;

  while (true) {
    const page = await fetchChatHistory(client, sessionKey, RELATION_HISTORY_PAGE_SIZE, before);
    if (page.length === 0) {
      fullyScanned = true;
      break;
    }

    if (!newestCursor) {
      newestCursor = historyCursor(page[page.length - 1]);
    }

    const extracted = extractHierarchyRelations(page);
    for (const childSessionKey of extracted.childSessionKeys) {
      childSessionKeys.add(childSessionKey);
    }
    if (!parentSessionKey && extracted.parentSessionKey) {
      parentSessionKey = extracted.parentSessionKey;
    }

    const oldestCursor = historyCursor(page[0]);
    const reachedKnownTail = stopAtCursor
      ? page.some((message) => historyCursor(message) === stopAtCursor)
      : false;

    if (reachedKnownTail) {
      break;
    }

    if (page.length < RELATION_HISTORY_PAGE_SIZE || !oldestCursor || before === oldestCursor) {
      fullyScanned = true;
      break;
    }

    before = oldestCursor;
  }

  return {
    childSessionKeys: [...childSessionKeys],
    parentSessionKey,
    newestCursor,
    fullyScanned,
  };
}

function shouldScanRelations(
  cached: RelationCacheEntry | undefined,
  updatedAt: number | null,
  isVisible: boolean,
  now: number,
): boolean {
  if (!cached) return true;
  if (cached.scanFailed) {
    return now - cached.lastScannedAt >= RELATION_SCAN_INTERVAL_MS;
  }
  if (!cached.fullyScanned) return true;
  if (!isVisible) return false;
  return cached.lastScannedUpdatedAt !== updatedAt;
}

function resolveVisibleParentSessionKey(params: {
  gatewayId: string;
  sessionKey: string;
  visibleSessionIds: Set<string>;
  parentBySession: Map<string, string>;
}): string | null {
  const { gatewayId, sessionKey, visibleSessionIds, parentBySession } = params;
  const visited = new Set<string>();
  let current = parentBySession.get(compositeSessionId(gatewayId, sessionKey)) ?? null;

  while (current) {
    const currentId = compositeSessionId(gatewayId, current);
    if (visibleSessionIds.has(currentId)) {
      return current;
    }
    if (visited.has(currentId)) {
      return null;
    }
    visited.add(currentId);
    current = parentBySession.get(currentId) ?? null;
  }

  return null;
}

// ── Hook ──

export function useAgentTicker(params: {
  gateways: GatewayConfig[];
  connectionStates: Map<string, GatewayStatus>;
  getClient: (gatewayId: string) => WSClient | null;
}): UseAgentTickerReturn {
  const { gateways, connectionStates, getClient } = params;

  // Ticker items indexed by composite ID
  const [itemMap, setItemMap] = useState<Map<string, TickerItem>>(new Map());

  // Ref for itemMap so the poll callback doesn't depend on state directly
  const itemMapRef = useRef(itemMap);
  itemMapRef.current = itemMap;

  // Manually dismissed item IDs → timestamp when dismissed
  // Loaded from sessionStorage on init
  const [dismissed, setDismissed] = useState<Map<string, number>>(() =>
    loadDismissedFromStorage(),
  );
  const dismissedRef = useRef(dismissed);
  dismissedRef.current = dismissed;

  // Subscribed session keys (show in main chat) — loaded from sessionStorage
  const [subscribedSessions, setSubscribedSessions] = useState<Set<string>>(() =>
    loadSubscribedFromStorage(),
  );

  // Track whether this is the initial load (for 24h filter)
  const isInitialLoadRef = useRef(true);

  // Heat tick — forces re-computation of heat levels every 5 seconds
  const [heatTick, setHeatTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setHeatTick(t => t + 1), 5000);
    return () => clearInterval(timer);
  }, []);

  // Peek state
  const [peek, setPeek] = useState<PeekData | null>(null);

  // Track poll timer
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Track event listeners for cleanup
  const eventListenersRef = useRef<Map<string, (frame: EventFrame) => void>>(new Map());

  // Refs for stable poll callback (avoid re-creating interval on every state change)
  const gatewaysRef = useRef(gateways);
  gatewaysRef.current = gateways;
  const connectionStatesRef = useRef(connectionStates);
  connectionStatesRef.current = connectionStates;
  const getClientRef = useRef(getClient);
  getClientRef.current = getClient;

  const relationCacheRef = useRef<Map<string, RelationCacheEntry>>(new Map());

  // ── Poll all connected gateways for subagent sessions ──
  const pollSessions = useCallback(async () => {
    const currentGateways = gatewaysRef.current;
    const currentConnectionStates = connectionStatesRef.current;
    const currentGetClient = getClientRef.current;
    const currentItemMap = itemMapRef.current;
    const currentDismissed = dismissedRef.current;
    const isInitial = isInitialLoadRef.current;
    const updates = new Map<string, TickerItem>();
    const relationCache = relationCacheRef.current;
    const visibleSessionIds = new Set<string>();
    const relationCandidatesById = new Map<string, {
      gatewayId: string;
      sessionKey: string;
      updatedAt: number | null;
      client: WSClient;
    }>();
    const now = Date.now();

    for (const gw of currentGateways) {
      const status = currentConnectionStates.get(gw.id);
      if (status !== 'connected') continue;

      const client = currentGetClient(gw.id);
      if (!client) continue;

      try {
        const result = await listSessions(client, {
          includeDerivedTitles: true,
          includeLastMessage: true,
          activeMinutes: 30, // Only recent sessions
        });

        for (const session of result.sessions) {
          if (!isSubagentSession(session.key)) continue;

          const id = compositeSessionId(gw.id, session.key);
          const existingItem = currentItemMap.get(id);
          const inferredStatus = inferStatus(session);

          // On initial load, filter out sessions older than 24 hours
          if (isInitial && session.updatedAt) {
            const age = now - session.updatedAt;
            if (age > INITIAL_LOAD_MAX_AGE_MS) continue;
          }

          // Check if this was dismissed but has new activity
          const dismissedAt = currentDismissed.get(id);
          if (dismissedAt && session.updatedAt && session.updatedAt > dismissedAt) {
            // Session has new activity since dismissal — un-dismiss it
            setDismissed((prev) => {
              const next = new Map(prev);
              next.delete(id);
              saveDismissedToStorage(next);
              return next;
            });
          }

          // Auto-subscribe new sessions if setting is enabled
          const isNewItem = !currentItemMap.has(id);
          const depthSetting = parseInt(localStorage.getItem('subagent-depth') ?? '0', 10);
          const sessionDepth = (session.key.match(/:subagent:/g) || []).length;
          if (isNewItem && depthSetting > 0 && sessionDepth <= depthSetting) {
            setSubscribedSessions((prev) => {
              if (prev.has(session.key)) return prev;
              const next = new Set(prev);
              next.add(session.key);
              saveSubscribedToStorage(next);
              return next;
            });
          }

          updates.set(id, {
            id,
            sessionKey: session.key,
            gatewayId: gw.id,
            gatewayName: gw.name,
            label: extractLabel(session),
            status: inferredStatus,
            firstSeenAt: existingItem?.firstSeenAt ?? now,
            completedAt:
              inferredStatus !== 'running'
                ? existingItem?.completedAt ?? now
                : null,
            lastMessagePreview: session.lastMessagePreview ?? undefined,
            updatedAt: session.updatedAt,
            sessionGone: false,
            goneAt: null,
            heatLevel: computeHeatLevel(session.updatedAt, false),
            parentSessionKey: existingItem?.parentSessionKey ?? null,
          });

          relationCandidatesById.set(id, {
            gatewayId: gw.id,
            sessionKey: session.key,
            updatedAt: session.updatedAt,
            client,
          });
          visibleSessionIds.add(id);
        }
      } catch (err) {
        console.warn(`[AgentTicker] Failed to poll sessions for ${gw.name}:`, err);
      }
    }

    const relationQueue = [...relationCandidatesById.values()];
    const queuedRelationIds = new Set(relationCandidatesById.keys());

    while (relationQueue.length > 0) {
      const candidate = relationQueue.shift();
      if (!candidate) continue;

      const cacheKey = compositeSessionId(candidate.gatewayId, candidate.sessionKey);
      const cached = relationCache.get(cacheKey);
      const isVisible = visibleSessionIds.has(cacheKey);

      if (shouldScanRelations(cached, candidate.updatedAt, isVisible, now)) {
        try {
          const scanResult = await discoverHierarchyRelations({
            client: candidate.client,
            sessionKey: candidate.sessionKey,
            stopAtCursor:
              cached?.fullyScanned && cached.newestCursor
                ? cached.newestCursor
                : null,
          });

          relationCache.set(cacheKey, {
            lastScannedAt: Date.now(),
            lastScannedUpdatedAt: candidate.updatedAt,
            childSessionKeys: [
              ...new Set([
                ...(cached?.childSessionKeys ?? []),
                ...scanResult.childSessionKeys,
              ]),
            ],
            parentSessionKey: scanResult.parentSessionKey ?? cached?.parentSessionKey ?? null,
            newestCursor: scanResult.newestCursor ?? cached?.newestCursor ?? null,
            fullyScanned: (cached?.fullyScanned ?? false) || scanResult.fullyScanned,
            scanFailed: false,
          });
        } catch (err) {
          console.warn(`[AgentTicker] Failed to scan hierarchy for ${candidate.sessionKey}:`, err);
          relationCache.set(cacheKey, {
            lastScannedAt: Date.now(),
            lastScannedUpdatedAt: candidate.updatedAt,
            childSessionKeys: cached?.childSessionKeys ?? [],
            parentSessionKey: cached?.parentSessionKey ?? null,
            newestCursor: cached?.newestCursor ?? null,
            fullyScanned: cached?.fullyScanned ?? false,
            scanFailed: true,
          });
        }
      }

      const resolvedCache = relationCache.get(cacheKey);
      if (!resolvedCache) continue;

      for (const childSessionKey of resolvedCache.childSessionKeys) {
        const childId = compositeSessionId(candidate.gatewayId, childSessionKey);
        if (queuedRelationIds.has(childId)) continue;

        const visibleChild = relationCandidatesById.get(childId);
        relationQueue.push({
          gatewayId: candidate.gatewayId,
          sessionKey: childSessionKey,
          updatedAt: visibleChild?.updatedAt ?? relationCache.get(childId)?.lastScannedUpdatedAt ?? null,
          client: visibleChild?.client ?? candidate.client,
        });
        queuedRelationIds.add(childId);
      }
    }

    const parentSessionKeys = new Map<string, string>();
    for (const [cacheKey, cache] of relationCache) {
      const separatorIndex = cacheKey.indexOf('::');
      if (separatorIndex < 0) continue;

      const gatewayId = cacheKey.slice(0, separatorIndex);
      const sessionKey = cacheKey.slice(separatorIndex + 2);

      if (cache.parentSessionKey && !parentSessionKeys.has(cacheKey)) {
        parentSessionKeys.set(cacheKey, cache.parentSessionKey);
      }

      for (const childSessionKey of cache.childSessionKeys) {
        const childId = compositeSessionId(gatewayId, childSessionKey);
        if (!parentSessionKeys.has(childId)) {
          parentSessionKeys.set(childId, sessionKey);
        }
      }
    }

    for (const [id, item] of updates) {
      updates.set(id, {
        ...item,
        parentSessionKey:
          resolveVisibleParentSessionKey({
            gatewayId: item.gatewayId,
            sessionKey: item.sessionKey,
            visibleSessionIds,
            parentBySession: parentSessionKeys,
          })
          ?? item.parentSessionKey
          ?? null,
      });
    }

    // After first successful poll, disable the initial load filter
    if (isInitial && updates.size >= 0) {
      isInitialLoadRef.current = false;
    }

    // Detect gone sessions: existing items whose gateway is connected
    // but whose session key was NOT in the latest poll results.
    // Build a set of connected gateway IDs for this poll cycle.
    const connectedGatewayIds = new Set<string>();
    for (const gw of currentGateways) {
      if (currentConnectionStates.get(gw.id) === 'connected') {
        connectedGatewayIds.add(gw.id);
      }
    }

    // Merge updates into existing items (preserve firstSeenAt for existing items)
    setItemMap((prev) => {
      const next = new Map(prev);

      for (const [id, item] of updates) {
        const existing = next.get(id);
        if (existing) {
          // Update existing item, preserve firstSeenAt
          next.set(id, {
            ...item,
            firstSeenAt: existing.firstSeenAt,
            completedAt: item.status !== 'running'
              ? existing.completedAt ?? now
              : null,
            heatLevel: computeHeatLevel(item.updatedAt, item.sessionGone),
          });
        } else {
          next.set(id, item);
        }
      }

      // Mark sessions as gone if their gateway is connected but they
      // were NOT returned in this poll cycle.
      for (const [id, item] of next) {
        if (updates.has(id)) continue; // Still present in poll results
        if (!connectedGatewayIds.has(item.gatewayId)) continue; // Gateway offline — can't determine

        if (!item.sessionGone) {
          // First time detecting this session as gone
          next.set(id, {
            ...item,
            sessionGone: true,
            goneAt: now,
            status: 'completed',
            completedAt: item.completedAt ?? now,
            heatLevel: 0,
          });
        }
        // If already marked sessionGone, keep existing values (no-op)
      }

      return next;
    });
  }, []); // Stable — uses refs for all dependencies

  // ── Start/stop polling ──
  useEffect(() => {
    // Initial poll
    void pollSessions();

    // Set up interval
    pollTimerRef.current = setInterval(() => {
      void pollSessions();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [pollSessions]);

  // ── Listen for chat events from subagent sessions → trigger refresh ──
  // Track client references so we can properly remove listeners on cleanup
  const clientRefsForListeners = useRef<Map<string, WSClient>>(new Map());

  useEffect(() => {
    const listeners = eventListenersRef.current;
    const clientRefs = clientRefsForListeners.current;

    for (const gw of gateways) {
      const status = connectionStates.get(gw.id);
      const client = getClient(gw.id);

      if (status === 'connected' && client && !listeners.has(gw.id)) {
        const handler = (frame: EventFrame) => {
          if (frame.event !== 'chat') return;
          const payload = frame.payload as { sessionKey?: string };
          if (payload.sessionKey && isSubagentSession(payload.sessionKey)) {
            // Debounce: schedule a refresh after a short delay
            setTimeout(() => void pollSessions(), 1000);
          }
        };

        client.on('event', handler);
        listeners.set(gw.id, handler);
        clientRefs.set(gw.id, client);
      } else if (status !== 'connected' && listeners.has(gw.id)) {
        // Clean up listener for disconnected gateway
        const savedClient = clientRefs.get(gw.id);
        const handler = listeners.get(gw.id)!;
        if (savedClient) {
          savedClient.off('event', handler);
        }
        listeners.delete(gw.id);
        clientRefs.delete(gw.id);
      }
    }

    return () => {
      // Clean up all listeners on unmount
      for (const [gwId, handler] of listeners) {
        const savedClient = clientRefs.get(gwId);
        if (savedClient) {
          savedClient.off('event', handler);
        }
      }
      listeners.clear();
      clientRefs.clear();
    };
  }, [gateways, connectionStates, getClient, pollSessions]);

  // ── Live streaming subscription for peek overlay ──
  // When a peek is open, subscribe to WSClient events filtered by sessionKey.
  // Streams deltas in real-time and finalizes messages on completion.
  const peekItemRef = useRef<TickerItem | null>(null);
  peekItemRef.current = peek?.item ?? null;

  useEffect(() => {
    if (!peek?.item) return;

    const { gatewayId, sessionKey } = peek.item;
    const peekItemId = peek.item.id;
    const client = getClient(gatewayId);
    if (!client) return;

    // Track accumulated text per runId for this peek session
    const activeStreams = new Map<string, { fullText: string; lastSeq: number }>();

    const handler = (frame: EventFrame) => {
      if (frame.event !== 'chat') return;

      const payload = frame.payload as ChatEventPayload;

      // Filter: only events for the peeked session
      if (!('sessionKey' in payload)) return;
      const eventSessionKey = (payload as { sessionKey?: string }).sessionKey;
      if (eventSessionKey !== sessionKey) return;

      switch (payload.state) {
        case 'delta': {
          const delta = payload as ChatEventDelta;
          const { runId, seq } = delta;
          const incomingText = normalizeContent(delta.message?.content ?? "");

          let stream = activeStreams.get(runId);
          if (!stream) {
            stream = { fullText: '', lastSeq: -1 };
            activeStreams.set(runId, stream);
          }

          // Skip duplicate/old sequence numbers
          if (seq <= stream.lastSeq) return;
          stream.lastSeq = seq;

          // Gateway sends accumulated text in each delta
          stream.fullText = incomingText;

          // Update peek streaming content
          setPeek((prev) => {
            if (!prev || prev.item.id !== peekItemId) return prev;
            return { ...prev, streamingContent: incomingText };
          });
          break;
        }

        case 'final': {
          const final = payload as ChatEventFinal;
          const { runId } = final;
          const finalContent = normalizeContent(final.message?.content ?? "");

          // Clean up stream tracking
          activeStreams.delete(runId);

          // Append finalized message and clear streaming
          setPeek((prev) => {
            if (!prev || prev.item.id !== peekItemId) return prev;

            const newMessage: HistoryMessage = {
              role: 'assistant',
              content: finalContent,
              ts: new Date().toISOString(),
            };

            // Trim to max messages to prevent unbounded growth
            const allMessages = [...prev.messages, newMessage];
            const trimmed = allMessages.length > PEEK_MAX_MESSAGES
              ? allMessages.slice(allMessages.length - PEEK_MAX_MESSAGES)
              : allMessages;

            return {
              ...prev,
              messages: trimmed,
              streamingContent: null,
            };
          });
          break;
        }

        case 'aborted':
        case 'error': {
          const { runId } = payload;
          activeStreams.delete(runId);

          setPeek((prev) => {
            if (!prev || prev.item.id !== peekItemId) return prev;
            return { ...prev, streamingContent: null };
          });
          break;
        }
      }
    };

    client.on('event', handler);

    return () => {
      client.off('event', handler);
      activeStreams.clear();
    };
    // Re-subscribe when the peeked item changes or getClient changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peek?.item?.id, getClient]);

  // ── Auto-dismiss gone sessions after delay ──
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    for (const [id, item] of itemMap) {
      if (!item.sessionGone || item.goneAt == null) continue;
      if (dismissed.has(id)) continue;

      const elapsed = Date.now() - item.goneAt;
      const remaining = AUTO_DISMISS_DELAY_MS - elapsed;

      if (remaining <= 0) {
        // Already past the delay — dismiss immediately
        setDismissed((prev) => {
          if (prev.has(id)) return prev;
          const next = new Map(prev);
          next.set(id, Date.now());
          saveDismissedToStorage(next);
          return next;
        });
      } else {
        // Schedule dismissal for the remaining time
        const timer = setTimeout(() => {
          setDismissed((prev) => {
            if (prev.has(id)) return prev;
            const next = new Map(prev);
            next.set(id, Date.now());
            saveDismissedToStorage(next);
            return next;
          });
        }, remaining);
        timers.push(timer);
      }
    }

    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, [itemMap, dismissed]);

  // ── Computed: sorted and filtered items ──
  const items = useMemo(() => {
    const filtered: TickerItem[] = [];

    for (const [id, item] of itemMap) {
      // Skip dismissed items
      if (dismissed.has(id)) continue;

      // Items with sessionGone should always show as completed
      if (item.sessionGone && item.status !== 'completed') {
        filtered.push({ ...item, status: 'completed' });
      } else {
        filtered.push(item);
      }
    }

    // Sort: running first, then by most recently active
    filtered.sort((a, b) => {
      // Running items come first
      if (a.status === 'running' && b.status !== 'running') return -1;
      if (a.status !== 'running' && b.status === 'running') return 1;

      // Among same-status items, sort by most recent activity
      const aTime = a.updatedAt ?? a.firstSeenAt;
      const bTime = b.updatedAt ?? b.firstSeenAt;
      return bTime - aTime;
    });

    // Recompute live heat levels (driven by heatTick for frequent updates)
    return filtered.map(item => ({
      ...item,
      heatLevel: computeHeatLevel(item.updatedAt, item.sessionGone),
    }));
  }, [itemMap, dismissed, heatTick]);

  const hasRunning = useMemo(
    () => items.some((item) => item.status === 'running'),
    [items],
  );

  const hasCompleted = useMemo(
    () => items.some((item) => item.status !== 'running'),
    [items],
  );

  // ── Peek: open ──
  const openPeek = useCallback(
    (item: TickerItem) => {
      setPeek({ item, messages: [], loading: true, error: null, streamingContent: null });

      const client = getClient(item.gatewayId);
      if (!client) {
        setPeek((prev) =>
          prev ? { ...prev, loading: false, error: 'Gateway not connected' } : null,
        );
        return;
      }

      fetchChatHistory(client, item.sessionKey, PEEK_MESSAGE_COUNT)
        .then((messages) => {
          setPeek((prev) =>
            prev && prev.item.id === item.id
              ? { ...prev, messages, loading: false }
              : prev,
          );
        })
        .catch((err) => {
          setPeek((prev) =>
            prev && prev.item.id === item.id
              ? {
                  ...prev,
                  loading: false,
                  error: err instanceof Error ? err.message : 'Failed to load',
                }
              : prev,
          );
        });
    },
    [getClient],
  );

  // ── Peek: close ──
  const closePeek = useCallback(() => {
    setPeek(null);
  }, []);

  // ── Dismiss ──
  const dismiss = useCallback((id: string) => {
    setDismissed((prev) => {
      const next = new Map(prev);
      next.set(id, Date.now());
      saveDismissedToStorage(next);
      return next;
    });
  }, []);

  // ── Dismiss all completed ──
  const dismissAllCompleted = useCallback(() => {
    const now = Date.now();
    const currentItems = itemMapRef.current;
    const currentDismissed = dismissedRef.current;

    // Collect IDs of all non-running, non-dismissed items
    const toDismiss: string[] = [];
    for (const [id, item] of currentItems) {
      if (item.status !== 'running' && !currentDismissed.has(id)) {
        toDismiss.push(id);
      }
    }
    if (toDismiss.length === 0) return;

    setDismissed((prev) => {
      const next = new Map(prev);
      for (const id of toDismiss) {
        next.set(id, now);
      }
      saveDismissedToStorage(next);
      return next;
    });
  }, []);

  // ── Manual refresh ──
  const refresh = useCallback(() => {
    void pollSessions();
  }, [pollSessions]);

  // ── Subscribe (show in main chat) ──
  const subscribe = useCallback((sessionKey: string) => {
    setSubscribedSessions((prev) => {
      const next = new Set(prev);
      next.add(sessionKey);
      saveSubscribedToStorage(next);
      return next;
    });
  }, []);

  // ── Unsubscribe (hide from main chat) ──
  const unsubscribe = useCallback((sessionKey: string) => {
    setSubscribedSessions((prev) => {
      const next = new Set(prev);
      next.delete(sessionKey);
      saveSubscribedToStorage(next);
      return next;
    });
  }, []);

  // ── Re-evaluate subscriptions when depth setting changes ──
  useEffect(() => {
    const handleDepthChange = (e: Event) => {
      const newDepth = parseInt((e as CustomEvent).detail ?? '0', 10);
      const currentItems = itemMapRef.current;

      for (const [, item] of currentItems) {
        const sessionDepth = (item.sessionKey.match(/:subagent:/g) || []).length;

        if (newDepth === 0) {
          // Off — unsubscribe everything
          unsubscribe(item.sessionKey);
        } else if (sessionDepth <= newDepth) {
          // Should be subscribed at this depth level
          subscribe(item.sessionKey);
        } else {
          // Too deep — unsubscribe
          unsubscribe(item.sessionKey);
        }
      }
    };

    window.addEventListener('subagent-depth-change', handleDepthChange);
    return () => window.removeEventListener('subagent-depth-change', handleDepthChange);
  }, [subscribe, unsubscribe]);

  // ── Check if subscribed ──
  const isSubscribed = useCallback((sessionKey: string) => {
    return subscribedSessions.has(sessionKey);
  }, [subscribedSessions]);

  return {
    items,
    hasRunning,
    hasCompleted,
    peek,
    openPeek,
    closePeek,
    dismiss,
    dismissAllCompleted,
    refresh,
    subscribedSessions,
    subscribe,
    unsubscribe,
    isSubscribed,
  };
}
